// ---------------------------------------------------------------------------
// WHAT EACH SCORING TERM ACTUALLY MEASURES
//
// Morgan, Aug 22: "annotate every scoring term in the decomposition with what
// underlying measurement it ultimately depends on... the code has accumulated
// rules with different names and stories that may now all be measuring the
// same thing."
//
// Each term below records its ROOT — the base measurement it bottoms out in.
// Terms sharing a root are candidates for double-counting, and the decomposition
// groups by root so duplicates surface without anyone having to notice them.
//
// This file makes no scoring decisions. It is a description of the ones that
// already exist.
// ---------------------------------------------------------------------------

export type TermFamily = "road" | "scenery" | "comfort" | "community" | "time" | "other";

/** Morgan, Aug 22: "tag its original intent... ranking, display, safety/honesty
 *  gate, or unknown. If a display-motivated rule changes ranking, flag it."
 *  Every value below is traced with `git log -S`, not inferred. */
export type RuleIntent = "ranking" | "display" | "safety-honesty" | "unknown";

export interface NonlinearRule {
  label: string;
  line: number;
  kind: "floor" | "ceiling" | "envelope";
  intent: RuleIntent;
  /** True when a rule introduced for how the BADGE reads also decides which
   *  route wins. This is the category Morgan asked to have flagged. */
  displayRuleChangesRanking?: boolean;
  provenance: string;
  note?: string;
}

/** Every nonlinear mutation of `pleasant`, in the order they execute.
 *  Order matters enormously: a later floor overrides an earlier ceiling. */
export const NONLINEAR_RULES: NonlinearRule[] = [
  { label: "communityAvoidPenalty", line: 1252, kind: "envelope", intent: "ranking",
    provenance: "0febd053, 17 Jun, 'Avoid/Not-nice ratings now heavily penalize roads'. Morgan, Rule 7.",
    note: "Floored at 0 while subtracting up to -60." },
  { label: "avoidHighwaysCeiling", line: 1281, kind: "ceiling", intent: "ranking",
    provenance: "Morgan's avoidHighways guardrail — 'a motorway-heavy route must not score high enough to be labeled pleasant'.",
    note: "hwy >= 50 -> ceiling 20; hwy >= 25 -> ceiling 45. NEUTRALISED: componentFloor runs later and lifts to 23." },
  { label: "namedArterialCeiling", line: 1321, kind: "ceiling", intent: "ranking",
    provenance: "'a hard cap so a mostly-highway route can never win the Pleasant slot over a real back-roads alternative'.",
    note: "namedArterial >= 60% -> 18; else 30. Also neutralised by the 23 floor at the 18 tier." },
  { label: "walkBorderlineCeiling", line: 1848, kind: "ceiling", intent: "ranking",
    provenance: "'keep in pool but cap the score so it won't win Pleasant'. Walking only." },
  { label: "bikeUnsafeCeiling", line: 1970, kind: "ceiling", intent: "safety-honesty",
    provenance: "Cycling: trunk/primary with no bike lane. Biking only." },
  { label: "brokenPromiseCeiling", line: 2048, kind: "ceiling", intent: "safety-honesty",
    provenance: "'a route that runs >25% on negative segments can never score above 30 — it broke the promise mid-route'." },
  { label: "epicHighwayLift", line: 2115, kind: "floor", intent: "unknown",
    provenance: "ba8518ff, 7 May, author gpt-engineer-app[bot], commit message in full: 'Changes'. No stated reason anywhere.",
    note: "Floors at 75 — above EVERY ceiling above it. The strongest single mutation in the file, and the least accounted for." },
  { label: "componentFloor", line: 2352, kind: "floor", intent: "display",
    displayRuleChangesRanking: true,
    provenance: "565629b2, 15 May, a 23-file commit titled 'Drive UX: bigger banner, speed HUD, lane guidance, adaptive zoom'. Its own comment: real-world feedback that 'if the drive is pleasant, we should not be getting 3/100 — floor relative to the strongest component SO THE BADGE STAYS INFORMATIVE.'",
    note: "DISPLAY-MOTIVATED, RANKING-EFFECTIVE. Applied to `pleasant`, which is also the ranking variable. With no OSM data preferenceMatch defaults to 50, landing in the >=35 branch, so the floor is 23. It runs AFTER the two ranking ceilings above, so it overrides both: a 90% motorway route is ceilinged to 20, then floored to 23. Observed in the offline replay: every motorway-heavy candidate printed exactly 23.0." },
  { label: "envelope0to100", line: 2364, kind: "envelope", intent: "safety-honesty",
    provenance: "Morgan's pl146 log, La Creche: additive boosts stacked past 100 and a 12-min main-road loop scored 146." },
];

/** The rules Morgan asked to have flagged: introduced for the badge, deciding
 *  the route. */
export function displayRulesThatRank(): NonlinearRule[] {
  return NONLINEAR_RULES.filter((r) => r.displayRuleChangesRanking);
}

/** Ceilings that a later floor can override. Order is the whole problem. */
export function overriddenCeilings(): Array<{ ceiling: NonlinearRule; overriddenBy: NonlinearRule }> {
  const out: Array<{ ceiling: NonlinearRule; overriddenBy: NonlinearRule }> = [];
  for (const c of NONLINEAR_RULES.filter((r) => r.kind === "ceiling")) {
    for (const f of NONLINEAR_RULES.filter((r) => r.kind === "floor" && r.line > c.line)) {
      out.push({ ceiling: c, overriddenBy: f });
    }
  }
  return out;
}

export interface TermSpec {
  /** Which family of ideas this belongs to. */
  family: TermFamily;
  /** The base measurement it ultimately depends on. Terms with the same root
   *  are measuring the same thing under different names. */
  root: string;
  /** Anything a reader needs in order to argue with it. */
  note?: string;
}

/** `highwayPct` = max(motorway+trunk+links, namedArterial) / distance.
 *  Since #139 (Morgan, 2 Jun) it EXCLUDES plain primary. */
const HWY = "highwayPct (motorway+trunk, or named arterial, whichever is larger)";

export const TERMS: Record<string, TermSpec> = {
  // ---- base weighted sum -------------------------------------------------
  "base.scenic":     { family: "scenery", root: "scenic component (parks, water, landmarks)", note: "weight 0.58, then cut to 0.25 by the profile blend at scoring.ts:1202" },
  "base.calm":       { family: "road",    root: HWY, note: "residentialPct*0.8 + (100-highwayPct)*0.2 — the FIRST place highwayPct is charged. Weight 0.36, then 0.25 after the blend." },
  "base.community":  { family: "community", root: "community tags", note: "weight 0.06" },
  "blend.profMatch": { family: "other",   root: "segment engine profile match", note: "replaces 75% of the score at scoring.ts:1202" },

  // ---- road character ----------------------------------------------------
  "namedArterialPenalty": { family: "road", root: HWY, note: "scoring.ts:1273. Fires at >=40% named arterial, up to -50 — the LARGEST single penalty in the file. Named arterials also feed highwayPct, so this shares a root with the three below." },
  "highwayCap":           { family: "road", root: HWY, note: "scoring.ts:1272. A CEILING, not a charge: when the user avoids highways and the route is >=25% highway, pleasant is truncated to 45 (20 above 50%). A route carrying +45 ocean and +28 scenic can be slammed to 45 — every point of scenery above the ceiling is discarded. Only fires when avoidHighways is on." },
  "arterialPenalty":      { family: "road", root: HWY, note: "scoring.ts:1927. (highwayPct-5)*0.70, net clamped to [-45,+12] with the backroad boost." },
  "primaryPenalty":       { family: "road", root: HWY, note: "scoring.ts:1626. STALE PURPOSE: its comment says highwayPct 'covers motorway+trunk+primary'. That stopped being true at #139. It now fires on the identical variable as arterialPenalty." },
  "backroadBoost":        { family: "road", root: "residentialPct", note: "scoring.ts:1928. (residentialPct-40)*0.15 — true ceiling +9, not +12; the +12 caps the NET of boost minus arterialPenalty." },
  "fastArterialPenalty":  { family: "road", root: "fastArterialKm (maxspeed >= 40mph)", note: "scoring.ts:1595. Correlates with highwayPct but is a separate measurement." },
  "fourLanePenalty":      { family: "road", root: "fourLaneKm (OSM lanes >= 4)", note: "scoring.ts:1613. Deliberately kept separate from fastArterial — width, not speed. Scaled up on short trips." },
  "slowStreetBoost":      { family: "road", root: "slowStreetKm (maxspeed <= 30mph or residential)" },
  "bywayBoost":           { family: "road", root: "curated byway list" },
  "epicAdj":              { family: "road", root: "epicHighwayMeters (curated great drives)", note: "also refunds highway penalties" },

  "componentFloor":  { family: "other", root: "max(community, scenic, preferenceMatch)", note: "scoring.ts:2348. A FLOOR, not a charge. PROVENANCE (git log -S): landed 15 May in 565629b2, a 23-file commit titled 'Drive UX: bigger banner, speed HUD, lane guidance, adaptive zoom'. Its own comment gives the reason: real-world feedback that 'if the drive is pleasant, we should not be getting 3/100 — floor relative to the strongest component SO THE BADGE STAYS INFORMATIVE.' That is a DISPLAY fix. It was applied to `pleasant`, which is also the ranking variable, so it silently became a ranking floor. Someone later noticed the default-50 problem and tightened the first branch from >=50 to >50 — but 50 then falls into the >=35 branch and floors at 23 instead of 28. The bug was seen, described correctly, and moved rather than removed." },
  "epicHighwayLift": { family: "road",  root: "epicHighwayMeters", note: "scoring.ts:2114. Floors pleasant at 75 when avoidHighways is on and the route runs an epic drive. PROVENANCE: ba8518ff, 7 May, authored by gpt-engineer-app[bot], commit message in full: 'Changes'. No stated reason anywhere. The weakest provenance of any rule in this file." },
  "envelope0to100":  { family: "other", root: "clamp", note: "scoring.ts:2361. Final 0-100 envelope." },
  "communityAvoidPenalty": { family: "community", root: "community Avoid / Not-nice tags", note: "scoring.ts:1252. CORRECTION: I first labelled this a sixth charge on highwayPct. It is not. git log -S traced it to 0febd053 (17 Jun), 'Avoid/Not-nice ratings now heavily penalize roads' — it is Rule 7's cost half, up to -60 from community tags, floored at 0. There are FIVE charges on highwayPct, not six." },
  "backroadAdj.applied": { family: "road", root: HWY, note: "the clamped NET of backroadBoost and arterialPenalty; both are reported raw beside it" },

  // ---- scenery -----------------------------------------------------------
  "waterBoost":       { family: "scenery", root: "water metres near route", note: "up to +36" },
  "oceanBoost":       { family: "scenery", root: "coast metres near route", note: "up to +45 — the single largest reward in the file" },
  "coastalAdj":       { family: "scenery", root: "coast metres near route", note: "shares a root with oceanBoost" },
  "waterTieBonus":    { family: "scenery", root: "waterFrac", note: "up to +6; a third term rooted in water" },
  "scenicAttractor":  { family: "scenery", root: "parks + historic + water counts", note: "up to +28 SHARED between parks, historic and water — an inland route competes for a slice of this while a coastal one gets +45 of its own" },
  "landmarkBoost":    { family: "scenery", root: "named landmark count", note: "up to +18" },
  "districtBoost":    { family: "scenery", root: "named district count", note: "up to +8" },
  "treeBoost":        { family: "scenery", root: "tree canopy" },
  "shadeBoost":       { family: "comfort", root: "tree canopy", note: "shares a root with treeBoost" },
  "townBonus":        { family: "scenery", root: "town quality list" },
  "prettyBoost":      { family: "scenery", root: "pretty-street signals (walking)" },
  "hudsonAdj":        { family: "scenery", root: "Hudson corridor coverage" },
  "regionRoadAdj":    { family: "scenery", root: "regional curated road coverage" },
  "continuityAdj":    { family: "scenery", root: "scenery continuity along the route" },
  "waterDominancePenalty": { family: "scenery", root: "water names vs parks/historic + extra time", note: "scoring.ts:2051. Penalises water-heavy detours when the user did NOT ask for water." },

  // ---- comfort / environment --------------------------------------------
  "rainPenalty": { family: "comfort", root: "forecast rain" },
  "tempPenalty": { family: "comfort", root: "forecast temperature" },
  "windPenalty": { family: "comfort", root: "forecast wind" },
  "aqiPenalty":  { family: "comfort", root: "air quality index" },
  "litAdj":      { family: "comfort", root: "street lighting (lit=yes)" },
  "noiseAdj":    { family: "comfort", root: "noise / intensity signal" },
  "walkAdj":     { family: "comfort", root: "walking safety signals" },
  "walkBoost":   { family: "comfort", root: "walking amenity signals" },
  "bikeAdj":     { family: "comfort", root: "cycling infrastructure" },

  // ---- community ---------------------------------------------------------
  "avoidPenalty": { family: "community", root: "community avoid tags", note: "Rule 7" },

  // ---- time --------------------------------------------------------------
  "timeOfDayAdj":    { family: "time", root: "time of day heuristics", note: "capped +/-8" },
  "trafficPenalty":  { family: "time", root: "live/predicted congestion" },
  "trafficPenalty2": { family: "time", root: "live/predicted congestion", note: "second congestion charge — shares a root with trafficPenalty" },
};

/** Terms grouped by the measurement they bottom out in. Any root with more
 *  than one term is charging the same characteristic more than once. */
export function termsByRoot(): Map<string, string[]> {
  const m = new Map<string, string[]>();
  for (const [label, spec] of Object.entries(TERMS)) {
    const list = m.get(spec.root) ?? [];
    list.push(label);
    m.set(spec.root, list);
  }
  return m;
}

/** Roots charged by more than one term — the duplicate detector. */
export function duplicatedRoots(): Array<{ root: string; terms: string[] }> {
  return [...termsByRoot().entries()]
    .filter(([, terms]) => terms.length > 1)
    .map(([root, terms]) => ({ root, terms }))
    .sort((a, b) => b.terms.length - a.terms.length);
}
